import { Update } from '@crehana/compass.icons.outline/v2/update';
import {
  createColumns,
  DataTableNamespace as DataTable,
  useTableState,
} from '@crehana/compass.ui/v3/table';
import { Tag } from '@crehana/compass.ui/v3/tag';
import { BasicTooltip } from '@crehana/compass.ui/v3/tooltip';
import { parseISO } from 'date-fns';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { PAGE_SIZES, STATUS_CONFIG } from '../../constants';
import { useTranslation } from '../../i18n';
import type { TMyOvertimeRequest } from '../../types';
import { formatMinutes } from '../../utils';
import { RequestDetailDrawer } from '../request-detail-drawer';
import { RequestRowActions } from './components/request-row-actions';
import { RequestsEmpty } from './components/requests-empty';

type TRequestsTableProps = {
  items: TMyOvertimeRequest[];
  totalCount: number;
  loading: boolean;
  error: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRetry: () => void;
  onCreateClick: () => void;
  onRequestCancelled: () => void;
};

export const RequestsTable = ({
  items,
  totalCount,
  loading,
  error,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRetry,
  onCreateClick,
  onRequestCancelled,
}: TRequestsTableProps) => {
  const { t, languageCode } = useTranslation();

  const [detailRequest, setDetailRequest] = useState<TMyOvertimeRequest | null>(
    null,
  );

  const tableState = useTableState({
    pagination: { pageIndex: page - 1, pageSize },
    // TanStack's `setPageSize` keeps the top ROW visible and recomputes the
    // page index from it — right for client pagination, wrong for our
    // server-paginated table. A size change must reset to page 1, never
    // forward the recomputed index (TIM-560 review finding CRITICAL-1).
    onPaginationChange: pagination => {
      if (pagination.pageSize !== pageSize) {
        onPageSizeChange(pagination.pageSize);
        return;
      }
      onPageChange(pagination.pageIndex + 1);
    },
  });

  const columns = createColumns<TMyOvertimeRequest>(({ column, cells }) => [
    column.accessor('request_date', {
      header: t('TABLE_HEADER_DATE'),
      // `request_date` is a date-only string. `parseISO` reads it as LOCAL
      // midnight, so the rendered day matches the day the collaborator
      // picked in every timezone. `cells.date` on a raw string reads UTC
      // midnight instead and rolls the day back west of UTC (TIM-560).
      cell: cells.date(row => ({
        value: parseISO(row.request_date),
        locale: languageCode,
      })),
    }),
    column.display({
      id: 'shift',
      header: t('TABLE_HEADER_SHIFT'),
      // The admin table reads this column with `cells.text`, which cannot
      // carry a tooltip. A long shift name has to clip AND stay readable, so
      // the cell is written by hand with the same typography the helper
      // applies. Clipping needs three things at once and drops in silence if
      // one goes missing: the span is a block, because an inline box ignores
      // overflow; the span clips; and the cell carries a literal width cap,
      // because the default table layout grows a cell to its content instead
      // of honouring `size` (TIM-223 hit this same trap). The cap must stay a
      // literal string — Tailwind reads source text.
      cellClassName: 'tw-max-w-[150px]',
      cell: ({ row }) => {
        const shiftName = row.original.shift_name;

        if (!shiftName) {
          return <span className="tw-cds-font-body-01">—</span>;
        }

        return (
          <BasicTooltip content={shiftName}>
            <span className="tw-cds-font-body-01 tw-block tw-truncate">
              {shiftName}
            </span>
          </BasicTooltip>
        );
      },
    }),
    column.display({
      id: 'extra-time',
      header: t('TABLE_HEADER_OVERTIME'),
      cell: cells.text(row => ({
        title: formatMinutes(row.final_extra_minutes ?? row.extra_minutes),
      })),
    }),
    column.display({
      id: 'compensation-time',
      header: t('TABLE_HEADER_COMPENSATION'),
      cell: cells.text(row => ({
        title: formatMinutes(
          row.final_replacement_minutes ?? row.replacement_minutes,
        ),
      })),
    }),
    column.display({
      id: 'reason',
      header: t('TABLE_HEADER_REASON'),
      cell: ({ row }) => (
        <p className="tw-line-clamp-2 tw-cds-font-body-02 tw-cds-text-primary tw-min-w-0 tw-break-words">
          {row.original.reason}
        </p>
      ),
    }),
    column.display({
      id: 'status',
      header: t('TABLE_HEADER_STATUS'),
      cell: ({ row }) => {
        const config = STATUS_CONFIG[row.original.status];
        return (
          <div className="tw-flex tw-items-center tw-cds-gap-2">
            <Tag variant={config.variant}>{t(config.tKey)}</Tag>
            {row.original.has_final_amount_difference ? (
              <BasicTooltip content={t('STATUS_UPDATED_TOOLTIP')}>
                <span
                  data-testid="final-amount-difference-icon"
                  className="tw-inline-flex tw-cds-text-secondary"
                >
                  <Update size={16} />
                </span>
              </BasicTooltip>
            ) : null}
          </div>
        );
      },
    }),
    column.display({
      id: 'actions',
      header: t('TABLE_HEADER_ACTIONS'),
      cell: ({ row }) => (
        <RequestRowActions
          request={row.original}
          onCancelled={onRequestCancelled}
          onViewDetail={setDetailRequest}
        />
      ),
    }),
  ]);

  const emptyState = (): ReactNode => {
    if (error) {
      return (
        <DataTable.Error
          title={t('TABLE_ERROR_TITLE')}
          description={t('TABLE_ERROR_DESCRIPTION')}
          retryLabel={t('ERROR_STATE_RETRY')}
          onRetry={onRetry}
        />
      );
    }
    if (items.length === 0) {
      return <RequestsEmpty onCreateClick={onCreateClick} />;
    }
    return undefined;
  };

  const resolvedEmptyState = emptyState();

  return (
    <>
      <DataTable
        columns={columns}
        data={error ? [] : items}
        rowCount={error ? 0 : totalCount}
        state={tableState}
        density="default"
        locale={languageCode}
        loading={loading}
        emptyState={resolvedEmptyState}
        footer={
          resolvedEmptyState ? undefined : (
            <DataTable.Footer>
              <DataTable.PageSizeSelector sizes={PAGE_SIZES} />
              <DataTable.Pagination />
            </DataTable.Footer>
          )
        }
      />
      <RequestDetailDrawer
        request={detailRequest}
        open={detailRequest !== null}
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            setDetailRequest(null);
          }
        }}
      />
    </>
  );
};
